// ─────────────────────────────────────────────────────────────────────────────
// Tokens (padrão Ciclo)
// ─────────────────────────────────────────────────────────────────────────────
export const colors = {
  bg: "#F5F2EA",
  surface: "#FFFFFF",
  border: "#E5E1D5",
  borderStrong: "#D6D1C2",
  text: "#1A1A1A",
  textMuted: "#5C5C5C",
  textSoft: "#8A8A82",

  accent: "#2A75B0",
  accentSoft: "#E2EDF7",
  good: "#1F8A4F",
  goodSoft: "#E2F1E8",
  bad: "#C53D2E",
  badSoft: "#FBE5E2",
  warn: "#E08A1E",
  warnSoft: "#FBEFD9",
};

export const fonts = {
  body: "'Geist', -apple-system, sans-serif",
  mono: "'Geist Mono', monospace",
};

export const globalCSS = `
  @import url('https://fonts.googleapis.com/css2?family=Geist:wght@300;400;500;600;700;800;900&family=Geist+Mono:wght@400;500;600&display=swap');

  *, *::before, *::after { box-sizing: border-box; }

  @keyframes labels-pulse {
    0%   { opacity: 0.45; }
    50%  { opacity: 1; }
    100% { opacity: 0.45; }
  }
`;

// ─────────────────────────────────────────────────────────────────────────────
// Page
// ─────────────────────────────────────────────────────────────────────────────
export const page = {
  background: colors.bg,
  minHeight: "100vh",
  padding: "24px 32px 40px",
  fontFamily: fonts.body,
  color: colors.text,
  fontFeatureSettings: "'tnum' 1, 'cv11' 1, 'ss01' 1",
  WebkitFontSmoothing: "antialiased",
};

export const dash = {
  display: "flex",
  flexDirection: "column",
  gap: 18,
  maxWidth: 1480,
  margin: "0 auto",
};

export const header = {
  wrap: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-end",
    flexWrap: "wrap",
    gap: 16,
  },
  title: {
    fontSize: 34,
    fontWeight: 800,
    letterSpacing: "-0.025em",
    lineHeight: 1,
    margin: 0,
  },
  subtitle: {
    fontSize: 15,
    color: colors.textMuted,
    letterSpacing: "0.01em",
    marginTop: 8,
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Card / Upload
// ─────────────────────────────────────────────────────────────────────────────
export const card = {
  background: colors.surface,
  border: `1px solid ${colors.border}`,
  borderRadius: 14,
  padding: 22,
};

export const uploadBox = (dragOver) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: 8,
  padding: "42px 20px",
  border: `2px dashed ${dragOver ? colors.accent : colors.borderStrong}`,
  background: dragOver ? colors.accentSoft : colors.bg,
  borderRadius: 12,
  cursor: "pointer",
  textAlign: "center",
  transition: "all 0.15s ease",
});

export const uploadTitle = {
  fontSize: 16,
  fontWeight: 700,
};

export const uploadHint = {
  fontSize: 13,
  color: colors.textSoft,
};

export const fileRow = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  flexWrap: "wrap",
  gap: 14,
  marginTop: 16,
};

export const fileInfo = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  fontSize: 14,
  fontWeight: 600,
};

export const fileBadge = {
  fontFamily: fonts.mono,
  fontSize: 12,
  fontWeight: 600,
  background: colors.accentSoft,
  color: colors.accent,
  borderRadius: 999,
  padding: "4px 12px",
};

// ─────────────────────────────────────────────────────────────────────────────
// Botões
// ─────────────────────────────────────────────────────────────────────────────
export const button = (variant = "primary", disabled = false) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
  padding: "10px 22px",
  borderRadius: 10,
  border:
    variant === "ghost" ? `1px solid ${colors.borderStrong}` : "1px solid transparent",
  background:
    variant === "primary"
      ? disabled
        ? colors.borderStrong
        : colors.text
      : variant === "danger"
      ? colors.badSoft
      : colors.surface,
  color:
    variant === "primary"
      ? "#FFFFFF"
      : variant === "danger"
      ? colors.bad
      : colors.text,
  fontFamily: fonts.body,
  fontSize: 14,
  fontWeight: 700,
  letterSpacing: "0.01em",
  cursor: disabled ? "not-allowed" : "pointer",
  opacity: disabled && variant !== "primary" ? 0.5 : 1,
  transition: "all 0.15s ease",
});

// ─────────────────────────────────────────────────────────────────────────────
// Progresso
// ─────────────────────────────────────────────────────────────────────────────
export const progressWrap = {
  display: "flex",
  flexDirection: "column",
  gap: 8,
  marginTop: 18,
};

export const progressLabel = {
  display: "flex",
  justifyContent: "space-between",
  fontSize: 13,
  fontWeight: 600,
  color: colors.textMuted,
  fontFamily: fonts.mono,
};

export const progressTrack = {
  height: 8,
  borderRadius: 999,
  background: "#ECEAE2",
  overflow: "hidden",
};

export const progressFill = (pct, hasError) => ({
  height: "100%",
  width: `${pct}%`,
  borderRadius: 999,
  background: hasError ? colors.warn : colors.good,
  transition: "width 0.25s ease",
});

// ─────────────────────────────────────────────────────────────────────────────
// Mensagens
// ─────────────────────────────────────────────────────────────────────────────
export const alert = (type) => ({
  padding: "12px 16px",
  borderRadius: 10,
  fontSize: 14,
  fontWeight: 600,
  background: type === "error" ? colors.badSoft : colors.warnSoft,
  color: type === "error" ? colors.bad : colors.warn,
  border: `1px solid ${type === "error" ? "#F0CCC6" : "#F0DDBC"}`,
});

// ─────────────────────────────────────────────────────────────────────────────
// Preview grid
// ─────────────────────────────────────────────────────────────────────────────
export const previewHeader = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  flexWrap: "wrap",
  gap: 12,
  marginBottom: 16,
};

export const previewTitle = {
  fontSize: 18,
  fontWeight: 800,
  letterSpacing: "-0.01em",
  margin: 0,
};

export const grid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
  gap: 14,
};

export const thumb = {
  wrap: {
    background: colors.surface,
    border: `1px solid ${colors.border}`,
    borderRadius: 10,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  },
  imgBox: (ratio) => ({
    width: "100%",
    paddingTop: `${ratio * 100}%`,
    position: "relative",
    background: "#FAFAF7",
    borderBottom: `1px solid ${colors.border}`,
  }),
  img: {
    position: "absolute",
    inset: 0,
    width: "100%",
    height: "100%",
    objectFit: "contain",
  },
  placeholder: (isError) => ({
    position: "absolute",
    inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 12,
    fontWeight: 700,
    fontFamily: fonts.mono,
    color: isError ? colors.bad : colors.textSoft,
    background: isError ? colors.badSoft : "#FAFAF7",
    animation: isError ? "none" : "labels-pulse 1.4s infinite",
  }),
  caption: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
    padding: "8px 10px",
    fontSize: 12,
  },
  index: {
    fontFamily: fonts.mono,
    fontWeight: 600,
    color: colors.textSoft,
  },
  ref: {
    fontWeight: 600,
    color: colors.textMuted,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
};
